/**
 * useEntitlementGrid — THE GRID, ON THE CLIENT (job #576). The client half of
 * api/_utils/entitlementGrid.ts.
 *
 * Tom's ruling, 2026-09-21 18:22Z: free languages are always free; premium
 * main flow is free to the end of yellow belt and £15 after; Listening Mode
 * as its own mode on a premium course is £15 from the first item; Script mode
 * is free everywhere; the £5 voice feature opens voice in free languages and
 * only the £15 plan opens it in premium ones. Everything shows at the
 * padlock and nowhere else.
 *
 * WHERE IT READS FROM: `grid` on GET /api/entitlement/user?course=<code> —
 * the endpoint the app already reads for entitlements and voice. The server
 * decides every cell; nothing here derives one from local state. The one
 * local shortcut is the course class: a course whose row says free or
 * community is never asked about, because there is no padlock to show.
 *
 * FAILS SHUT. A premium course whose grid cannot be read answers "not
 * allowed" for every paid cell — the padlock, with its own refresh as the
 * way back — never "allowed". A stale open is a learner walking through a
 * wall; a stale padlock is a learner tapping refresh.
 *
 * REFRESHES ON FOREGROUND AND ON 402. A learner who pays in Paddle's tab, or
 * is granted while the app sits open, must not keep a stale padlock. Every
 * loaded course is re-read when the app returns to the foreground, and a
 * route answering 402 invalidates and re-reads (noteEntitlementRefused, in
 * useVoiceEntitlement, calls back into here).
 */

import { computed, inject, ref, type Ref } from 'vue'
import type { SupabaseClient } from '@supabase/supabase-js'
import { inferPricingTier } from '@ssi/core'
import { resolveSupabase } from './schools/client'
import { bootFetch } from '../playback/signedInBoot'
import { registerEntitlementRefresher, type PadlockPlan } from './useVoiceEntitlement'

export type GridThing =
  | 'main_flow'
  | 'in_flow_pod'
  | 'listening_mode'
  | 'script_mode'
  | 'group_text'
  | 'group_voice_note'
  | 'huddle'
  | 'connect_call'

export interface GridCell {
  allowed: boolean
  thing: GridThing
  code?: 'plan_required' | 'voice_required'
  error?: string
  price?: { monthlyPence: number; currency: 'GBP'; priceId: string | null }
}

export interface CourseGrid {
  courseClass: 'free' | 'premium'
  grid: Record<GridThing, GridCell>
}

/** The course facts the shortcut needs: the same fields the server reads. */
export interface CourseClassFacts {
  course_code: string
  pricing_tier?: string | null
  is_community?: boolean | null
  target_lang?: string | null
}

/** Free or premium from the course row, exactly as the server's courseClassOf decides it. */
export function courseClassOf(course: CourseClassFacts): 'free' | 'premium' {
  const tier = course.pricing_tier ?? inferPricingTier(course.target_lang ?? '', course.course_code)
  const community = course.is_community ?? course.course_code.startsWith('community_')
  if (community || tier === 'community' || tier === 'free') return 'free'
  return 'premium'
}

/** Which checkout a refused cell sells. */
export function padlockPlanForCell(cell: GridCell | null | undefined): PadlockPlan {
  return cell?.code === 'voice_required' ? 'voice' : 'premium'
}

// Module-level: one answer per course, whichever screen asked.
const grids = ref<Record<string, CourseGrid>>({})
const inflight = new Map<string, Promise<CourseGrid | null>>()
const loading = ref(false)

let resetVersion = 0
let refresherRegistered = false

export function useEntitlementGrid() {
  const supabaseRef = inject<Ref<SupabaseClient | null> | null>('supabase', null)

  async function authToken(): Promise<string | null> {
    const sb = resolveSupabase(supabaseRef)
    if (!sb) return null
    try {
      const { data } = await sb.auth.getSession()
      return data?.session?.access_token ?? null
    } catch {
      return null
    }
  }

  /** Re-read one course's grid from the server. Null on any failure. */
  async function refresh(courseCode: string): Promise<CourseGrid | null> {
    if (!courseCode) return null
    const running = inflight.get(courseCode)
    if (running) return running
    const version = resetVersion
    const p = (async () => {
      loading.value = true
      try {
        const token = await authToken()
        if (version !== resetVersion) return null
        if (!token) {
          delete grids.value[courseCode]
          return null
        }
        const resp = await bootFetch(`/api/entitlement/user?course=${encodeURIComponent(courseCode)}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (version !== resetVersion) return null
        if (!resp.ok) {
          delete grids.value[courseCode]
          return null
        }
        const data = await resp.json().catch(() => ({}))
        if (version !== resetVersion) return null
        const g = data?.grid
        if (!g || !g.grid || (g.courseClass !== 'free' && g.courseClass !== 'premium')) {
          delete grids.value[courseCode]
          return null
        }
        const parsed: CourseGrid = { courseClass: g.courseClass, grid: g.grid }
        grids.value = { ...grids.value, [courseCode]: parsed }
        return parsed
      } catch {
        if (version !== resetVersion) return null
        delete grids.value[courseCode]
        return null
      } finally {
        if (version === resetVersion) {
          loading.value = false
          inflight.delete(courseCode)
        }
      }
    })()
    inflight.set(courseCode, p)
    return p
  }

  /** Every loaded course, re-read. The foreground and 402 hook. */
  async function refreshAll(): Promise<void> {
    await Promise.all(Object.keys(grids.value).map((c) => refresh(c)))
  }

  if (!refresherRegistered) {
    refresherRegistered = true
    registerEntitlementRefresher(refreshAll)
  }

  /** The known cell, or null when the course has not been read. Sync. */
  function cellSync(courseCode: string, thing: GridThing): GridCell | null {
    return grids.value[courseCode]?.grid?.[thing] ?? null
  }

  /**
   * The guard a paid door calls before opening. A free course is open with
   * no round trip. A premium course answers from the known grid, or one
   * read; an unreadable grid is a refused cell — fail shut.
   */
  async function ensure(course: CourseClassFacts, thing: GridThing): Promise<GridCell> {
    if (courseClassOf(course) === 'free') return { allowed: true, thing }
    const known = cellSync(course.course_code, thing)
    if (known?.allowed) return known
    const fresh = await refresh(course.course_code)
    const cell = fresh?.grid?.[thing]
    if (cell) return cell
    return { allowed: false, thing, code: 'plan_required' }
  }

  /** Forget everything, e.g. on sign-out. */
  function reset(): void {
    resetVersion++
    inflight.clear()
    loading.value = false
    grids.value = {}
  }

  return {
    grids: computed(() => grids.value),
    loading,
    refresh,
    refreshAll,
    ensure,
    cellSync,
    reset,
    courseClassOf,
    padlockPlanForCell,
  }
}
